"use client";

import { RotateCw, TriangleAlert } from "lucide-react";
import { useSyncExternalStore } from "react";

import { DEFAULT_POST_LANGUAGE } from "@/lib/post-language";
import { parsePostLanguageFromPathname } from "@/lib/post-routes";
import { getPublicVocabulary } from "@/lib/public-vocabulary";

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

// global-error rimpiazza il root layout: globals.css non e caricato, quindi la
// palette va ridichiarata qui e lo stile e inline. Niente Tailwind, niente font
// del sito, nessun hook di routing — deve reggere anche se il layout e rotto.
const PALETTE_CSS = `
:root {
  --bg: #05080b;
  --ink: #e9eff8;
  --ink-softer: #c2cfdf;
  --muted: #8899af;
  --surface: #0e1622;
  --surface-2: #121c2b;
  --line: #283446;
}
html, body { margin: 0; padding: 0; background: var(--bg); color: var(--ink); }
`;

const NEVER_CHANGES = () => () => {};

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  // usePathname dipende dal contesto del router, che qui puo essere proprio
  // cio che si e rotto: la lingua si legge da window con useSyncExternalStore,
  // che sull'HTML del server ricade sul default senza disallineare l'idratazione.
  const pathname = useSyncExternalStore(
    NEVER_CHANGES,
    () => window.location.pathname,
    () => "",
  );
  const language = parsePostLanguageFromPathname(pathname) ?? DEFAULT_POST_LANGUAGE;
  const copy = getPublicVocabulary(language).status;
  const detail = error.digest ? copy.errorDigest(error.digest) : null;

  return (
    <html lang={language}>
      <body
        style={{
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <meta name="robots" content="noindex, nofollow" />
        <style dangerouslySetInnerHTML={{ __html: PALETTE_CSS }} />
        <main
          style={{
            width: "100%",
            maxWidth: "560px",
            border: "1px solid var(--line)",
            borderRadius: "24px",
            background: "linear-gradient(180deg, var(--surface) 0%, var(--surface-2) 45%, var(--surface) 100%)",
            boxShadow: "0 30px 60px rgba(0,0,0,0.5)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              borderBottom: "1px solid var(--line)",
              background: "rgba(0,0,0,0.4)",
              color: "#fda4af",
              padding: "10px 16px",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "10px",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            {copy.errorEyebrow}
          </div>
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                border: "1px solid rgba(244,63,94,0.4)",
                background: "rgba(76,5,25,0.4)",
                color: "#fda4af",
              }}
            >
              <TriangleAlert width={24} height={24} />
            </span>
            <h1
              style={{
                margin: "16px 0 0",
                fontSize: "26px",
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "var(--ink)",
              }}
            >
              {copy.errorTitle}
            </h1>
            <p
              style={{
                margin: "12px auto 0",
                maxWidth: "40ch",
                fontSize: "15px",
                lineHeight: 1.6,
                color: "var(--ink-softer)",
              }}
            >
              {copy.errorDescription}
            </p>
            {detail ? (
              <p
                style={{
                  margin: "12px 0 0",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: "11px",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "var(--muted)",
                }}
              >
                {detail}
              </p>
            ) : null}
            <div
              style={{
                marginTop: "24px",
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={reset}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "6px",
                  border: "1px solid rgba(108,184,255,0.5)",
                  background: "rgba(108,184,255,0.1)",
                  color: "var(--ink)",
                  padding: "10px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                <RotateCw width={16} height={16} aria-hidden="true" />
                {copy.retry}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "6px",
                  border: "1px solid var(--line)",
                  background: "var(--surface-2)",
                  color: "var(--muted)",
                  padding: "10px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                {copy.reload}
              </button>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
